import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useEffect, useRef, useState } from 'react'
import FocusLock from 'react-focus-lock'
import { IoCloseSharp } from 'react-icons/io5'
import { useClickAway } from 'react-use'
import styled, { css } from 'styled-components'
import { media, phoneMaxWidth } from 'utils/media'
import { useEscapeKey } from 'utils/useEscapeKey'
import { Portal } from 'lib/Portal'
import { Text, textSize } from 'lib/Text'

export type ModalSize = 'auto' | 'fullScreen' | 'fit'

const Container = styled(motion.div)`
  background: rgba(0, 0, 0, 0.2);
  bottom: 0;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1000;
`

interface BoxProps {
  $modalSize: ModalSize
  $mobileFullscreen: boolean
}

const Box = styled(motion.div)<BoxProps>`
  --modal-row-padding: var(--wide-grid-spacing);
  background-color: white;
  border-radius: 8px;
  border: 0;
  display: flex;
  flex-direction: column;
  margin: 10vh auto;
  max-height: 80vh;
  max-width: ${phoneMaxWidth}px;
  position: relative;
  width: 100%;

  ${media.phoneDown} {
    --modal-row-padding: 16px;
    max-width: 90vw;
  }

  @media screen and (max-height: 600px) {
    margin: 12px auto 12px auto;
    max-height: calc(100vh - 24px);
  }

  ${(props) =>
    props.$modalSize === 'fullScreen' &&
    css`
      max-width: 95vw;
      max-height: 95vh;
      width: 100%;
      height: 100%;
      margin: 2.5vh 2.5vw;
    `}

  ${(props) =>
    props.$modalSize === 'fit' &&
    css`
      margin: 5vh auto;
      max-height: 90vh;
      width: min-content;
      ${media.phoneDown} {
        width: 100%;
      }
    `}

  ${(props) =>
    props.$mobileFullscreen &&
    css`
      ${media.phoneDown} {
        border-radius: 0;
        border: none;
        margin: 0;
        position: fixed;
        top: 0px;
        left: 0px;
        right: 0px;
        bottom: 0;
        max-height: none;
        max-width: none;
        overflow: hidden;
      }
    `}
`

const TopSection = styled.header`
  background-color: white;
  display: flex;
  border-radius: 8px 8px 0 0;
  border-bottom: 1px solid var(--neutral-20);
  padding: var(--modal-row-padding) var(--modal-row-padding) 12px var(--modal-row-padding);
  position: relative;
  min-height: 72px;

  ${media.phoneDown} {
    position: relative;
  }
`

const Title = styled(Text)`
  align-items: center;
  display: inline-flex;
  margin-right: 40px;
  min-height: 40px;
  ${media.phoneDown} {
    ${textSize('s')}
  }
`

const Content = styled.div`
  display: block;
  flex: 1;
  padding: var(--modal-row-padding);
  overflow-y: auto;

  ${media.phoneDown} {
    overflow: auto;
    height: -webkit-fill-available; // Respect the mobile toolbars
  }
`

const CloseButton = styled.button<{ withTitle: boolean }>`
  appearance: none;
  background: transparent;
  border: none;
  color: var(--neutral-100);
  cursor: pointer;
  display: flex;
  outline: none;

  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  padding: 8px;
  transition: transform 0.1s ease-in-out;
  &:hover {
    transform: translateY(-50%) scale(1.12);
  }
`

const Actions = styled.footer`
  border-top: 1px solid var(--neutral-20);
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding: var(--modal-row-padding);
`

export interface Props {
  open: boolean
  children: React.ReactNode
  onRequestClose?: (e?: React.MouseEvent | Event) => void
  title?: React.ReactNode
  size?: ModalSize
  mobileFullscreen?: boolean
  showClose?: boolean
  className?: string
  actions?: React.ReactNode
}

export const ModalPopupNew = ({
  open,
  children,
  onRequestClose = (e?: React.MouseEvent | Event) => {},
  title,
  size = 'auto',
  mobileFullscreen = true,
  showClose = true,
  className,
  actions
}: Props) => {
  const boxRef = useRef<HTMLDivElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [contentRefVisible, setContentRefVisible] = useState(false)

  useEscapeKey(onRequestClose, open)

  useClickAway(boxRef, (e) => {
    e.preventDefault()
    onRequestClose(e)
  })

  const handleCloseClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    onRequestClose(e)
  }

  useEffect(() => {
    if (contentRefVisible && contentRef.current) {
      if (open) {
        disableBodyScroll(contentRef.current)
      } else {
        enableBodyScroll(contentRef.current)
      }
    }

    return () => {
      clearAllBodyScrollLocks()
    }
  }, [open, contentRefVisible])

  return (
    <AnimatePresence>
      {open && (
        <Portal>
          <FocusLock disabled={false}>
            <Container initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <Box
                ref={boxRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={title ? 'modal-title' : undefined}
                $modalSize={size}
                $mobileFullscreen={mobileFullscreen}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 50 }}>
                {(title || showClose) && (
                  <TopSection>
                    {title && (
                      <Title id="modal-title" size="s">
                        {title}
                      </Title>
                    )}
                    {showClose && (
                      <CloseButton aria-label="Close" onClick={handleCloseClick} withTitle={!!title}>
                        <IoCloseSharp size={24} />
                      </CloseButton>
                    )}
                  </TopSection>
                )}
                <Content
                  className={className}
                  ref={(el) => {
                    contentRef.current = el
                    setContentRefVisible(!!el)
                  }}>
                  {children}
                </Content>
                {actions && <Actions>{actions}</Actions>}
              </Box>
            </Container>
          </FocusLock>
        </Portal>
      )}
    </AnimatePresence>
  )
}
